import React, { useCallback, useEffect, useRef, useState } from "react";
import { gaTrackCta } from "../../analytics/ga4";
import { C, NAV_ITEMS } from "../../core/constants";
import { pageGutterPx } from "../../core/breakpoints";
import { hexToRgba } from "../../core/color";
import { runtimeConfig } from "../../config/runtime";
import { localizedPath } from "../../core/localePaths";
import { useI18n } from "../../features/i18n/LocaleProvider";
import { signOutSession } from "../../features/session/api";
import { useMenuPosition } from "../../features/navigation/useMenuPosition";
import Button from "../../ui/Button";
import CreditsChip from "./CreditsChip";
import LanguageSwitcher from "./LanguageSwitcher";

export default function TopBar({
  acc,
  setPage,
  locale,
  setLocale,
  onLocaleNavigate = null,
  dashboardMeta,
  showUpgradeButton = true,
  analyticsScreen = "dashboard",
  isMobile = false,
  showCreditsChip = false,
  creditsBalance = 0,
}) {
  const { t } = useI18n();
  const screen = analyticsScreen;
  const [menuOpen, setMenuOpen] = useState(false);
  const [statusExpanded, setStatusExpanded] = useState(false);
  const [avatarHover, setAvatarHover] = useState(false);
  const avatarMenuRef = useRef(null);
  const avatarButtonRef = useRef(null);
  const menuPos = useMenuPosition(menuOpen, avatarButtonRef);
  const userInitial = (runtimeConfig.authContext.user?.email || "S").trim().charAt(0).toUpperCase();
  const pageTitle = NAV_ITEMS.find((item) => item.id === analyticsScreen)?.labelKey;
  const gutter = pageGutterPx(isMobile);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  const signOut = useCallback(async () => {
    try {
      await signOutSession();
    } catch (_) {
      // Redirect anyway so stale sessions don't trap users.
    } finally {
      window.location.assign(localizedPath("login", locale || "en"));
    }
  }, [locale]);

  const menuItemStyle = {
    width: "100%",
    border: "none",
    borderRadius: 10,
    background: "transparent",
    color: C.textSecondary,
    textAlign: "left",
    fontFamily: "'DM Sans',sans-serif",
    fontSize: 13,
    padding: "10px 12px",
    cursor: "pointer",
    transition: "all 0.2s ease",
  };

  const onMenuItemHover = (e, active) => {
    e.currentTarget.style.background = active ? `${acc.primary}1a` : "transparent";
    e.currentTarget.style.color = active ? C.textPrimary : C.textSecondary;
  };

  return (
    <div
      style={{
        height: isMobile ? 56 : 64,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        padding: `0 ${gutter}px`,
        background: "rgba(5,8,24,0.8)",
        backdropFilter: "blur(16px)",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        position: "sticky",
        top: 0,
        zIndex: 40,
      }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        {isMobile ? (
          <>
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 18, fontWeight: 500, color: acc.primary, lineHeight: 1.2 }}>
              {pageTitle ? t(pageTitle) : "✦ Ecliptica"}
            </div>
            <button
              type="button"
              onClick={() => setStatusExpanded((prev) => !prev)}
              style={{
                marginTop: 2,
                border: "none",
                background: "transparent",
                padding: 0,
                cursor: "pointer",
                textAlign: "left",
                maxWidth: "100%",
              }}
            >
              <span style={{ fontSize: 11, color: C.textMuted, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: statusExpanded ? "normal" : "nowrap" }}>
                {dashboardMeta.shortDate}
                {statusExpanded ? ` · ${dashboardMeta.cosmicStatus}` : ""}
              </span>
            </button>
          </>
        ) : (
          <>
            <span style={{ fontSize: 13, color: C.textMuted }}>{dashboardMeta.longDate} · </span>
            <span style={{ fontSize: 13, color: acc.primary }}>{dashboardMeta.cosmicStatus}</span>
          </>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 10 : 20, position: "relative", zIndex: 41, pointerEvents: "auto", flexShrink: 0 }}>
        {isMobile && showCreditsChip && (
          <CreditsChip
            acc={acc}
            creditsBalance={creditsBalance}
            activeSubscription={dashboardMeta.activeSubscription}
            compact
            onClick={() => setPage("creditsPlans")}
          />
        )}
        {!isMobile && !dashboardMeta.activeSubscription && showUpgradeButton ? (
          <Button
            variant="outline"
            acc={acc}
            onClick={() => {
              gaTrackCta({ cta_id: "topbar_upgrade", cta_label: t("upgrade"), destination: "creditsPlans", screen_class: screen });
              setPage("creditsPlans");
            }}
            style={{ padding: "7px 18px", fontSize: 13 }}
          >
            {t("upgrade")}
          </Button>
        ) : !isMobile && dashboardMeta.activeSubscription ? (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              borderRadius: 999,
              border: `1px solid ${acc.secondary}66`,
              background: `${acc.secondary}1a`,
              color: acc.secondary,
              fontSize: 11,
              letterSpacing: "0.08em",
              padding: "6px 12px",
              textTransform: "uppercase",
            }}
          >
            <span>✦</span>
            <span>{t("premium_badge")}</span>
          </div>
        ) : null}
        <LanguageSwitcher
          locale={locale}
          onChange={setLocale}
          acc={acc}
          onNavigate={onLocaleNavigate}
          shouldClose={menuOpen}
          onOpenChange={(open) => {
            if (open) setMenuOpen(false);
          }}
        />
        <div ref={avatarMenuRef} style={{ position: "relative", zIndex: 42, pointerEvents: "auto" }}>
          <button
            ref={avatarButtonRef}
            type="button"
            onMouseEnter={() => setAvatarHover(true)}
            onMouseLeave={() => setAvatarHover(false)}
            onFocus={() => setAvatarHover(true)}
            onBlur={() => setAvatarHover(false)}
            onPointerDown={(event) => {
              event.preventDefault();
              setMenuOpen((prev) => !prev);
            }}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            style={{
              height: 36,
              borderRadius: 999,
              border: `1px solid ${menuOpen || avatarHover ? `${acc.primary}cc` : `${acc.primary}66`}`,
              background:
                menuOpen || avatarHover
                  ? `linear-gradient(135deg,${acc.primary},${acc.tertiary})`
                  : `linear-gradient(135deg,${acc.primary}dd,${acc.tertiary}dd)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              padding: isMobile ? "0 8px" : "0 10px 0 8px",
              color: "#fff",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              boxShadow: menuOpen || avatarHover ? `0 0 22px ${hexToRgba(acc.primary, 0.55)}` : "0 0 0 transparent",
              transform: avatarHover ? "translateY(-1px)" : "none",
              transition: "all 0.2s ease",
              pointerEvents: "auto",
            }}
          >
            <span
              style={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.12)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              {userInitial}
            </span>
            {!isMobile && (
              <span
                style={{
                  fontSize: 9,
                  lineHeight: 1,
                  opacity: 0.9,
                  transform: menuOpen ? "rotate(180deg)" : "none",
                  transition: "transform 0.2s ease",
                  marginTop: 1,
                }}
              >
                ▾
              </span>
            )}
          </button>
          {menuOpen && (
            <div
              style={{
                position: "fixed",
                top: menuPos.top,
                right: menuPos.right,
                minWidth: 170,
                padding: 8,
                borderRadius: 12,
                border: `1px solid ${acc.primary}44`,
                background: "rgba(12, 18, 40, 0.95)",
                backdropFilter: "blur(12px)",
                boxShadow: `0 12px 30px rgba(0,0,0,0.45), 0 0 18px ${hexToRgba(acc.primary, 0.2)}`,
                zIndex: 80,
                pointerEvents: "auto",
              }}
            >
              {isMobile && !dashboardMeta.activeSubscription && showUpgradeButton && (
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    gaTrackCta({ cta_id: "topbar_upgrade", cta_label: t("upgrade"), destination: "creditsPlans", screen_class: screen });
                    setPage("creditsPlans");
                  }}
                  style={{ ...menuItemStyle, marginBottom: 4 }}
                  onMouseEnter={(e) => onMenuItemHover(e, true)}
                  onMouseLeave={(e) => onMenuItemHover(e, false)}
                >
                  {t("upgrade")}
                </button>
              )}
              {isMobile && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      gaTrackCta({ cta_id: "topbar_settings", cta_label: t("account_settings"), destination: "settings", screen_class: screen });
                      setPage("settings");
                    }}
                    style={menuItemStyle}
                    onMouseEnter={(e) => onMenuItemHover(e, true)}
                    onMouseLeave={(e) => onMenuItemHover(e, false)}
                  >
                    {t("account_settings")}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      gaTrackCta({ cta_id: "topbar_contact", cta_label: t("contact_us"), destination: "contact", screen_class: screen });
                      setPage("contact");
                    }}
                    style={{ ...menuItemStyle, marginBottom: 4 }}
                    onMouseEnter={(e) => onMenuItemHover(e, true)}
                    onMouseLeave={(e) => onMenuItemHover(e, false)}
                  >
                    {t("contact_us")}
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={async () => {
                  setMenuOpen(false);
                  await signOut();
                }}
                style={menuItemStyle}
                onMouseEnter={(e) => onMenuItemHover(e, true)}
                onMouseLeave={(e) => onMenuItemHover(e, false)}
              >
                {t("sign_out")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
